/**
 * Same-origin JSON client for the Offertli API (spec §24).
 *
 * - Base path `/api/v1`, `credentials: "same-origin"` (httpOnly session cookie).
 * - Normalizes the §24.2 error envelope into `ApiError` ({code, message, fields}).
 * - CSRF: the token is bootstrapped once from `GET /auth/me`, cached in module
 *   state and attached as `x-csrf-token` to every mutating request.
 * - A 401 outside of auth endpoints/pages redirects to /login.
 */
import type { Page } from "@offertli/shared";

export type { Page };

import { translateApiMessage } from "./api-messages.js";

const BASE = "/api/v1";

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly fields?: Record<string, string>;
  readonly requestId?: string;

  constructor(init: {
    code: string;
    message: string;
    status: number;
    fields?: Record<string, string>;
    requestId?: string;
  }) {
    // Shown to people as is, so translated once here.
    super(translateApiMessage(init.message));
    this.name = "ApiError";
    this.code = init.code;
    this.status = init.status;
    this.fields = init.fields
      ? Object.fromEntries(
          Object.entries(init.fields).map(([field, message]) => [
            field,
            translateApiMessage(message),
          ]),
        )
      : undefined;
    this.requestId = init.requestId;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/* ------------------------------------------------------------------ */
/* CSRF token management                                               */
/* ------------------------------------------------------------------ */

let csrfToken: string | null = null;
let csrfBootstrap: Promise<void> | null = null;

export function getCsrfToken(): string | null {
  return csrfToken;
}

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

/**
 * Bootstrap the CSRF token once via GET /auth/me. Safe to call repeatedly;
 * concurrent callers share one in-flight request. Resolves silently when the
 * caller is unauthenticated (401) — guards handle the redirect.
 */
export function bootstrapCsrf(): Promise<void> {
  csrfBootstrap ??= apiGet<{ csrfToken: string }>("/auth/me")
    .then((me) => {
      csrfToken = me.csrfToken;
    })
    .catch(() => {
      csrfToken = null;
    })
    .finally(() => {
      csrfBootstrap = null;
    });
  return csrfBootstrap;
}

/* ------------------------------------------------------------------ */
/* 401 handling                                                        */
/* ------------------------------------------------------------------ */

/** Pages that must remain reachable without a session. */
const PUBLIC_PAGE_PREFIXES = [
  "/login",
  "/forgot-password",
  "/reset-password",
  "/invite",
  "/setup",
  "/offline",
];

/** Endpoints whose 401 is an expected, handled outcome (never redirect). */
const NO_REDIRECT_ENDPOINTS = ["/auth/login", "/auth/me", "/auth/logout"];

let redirecting = false;

function handleUnauthorized(path: string): void {
  if (NO_REDIRECT_ENDPOINTS.some((p) => path.startsWith(p))) return;
  const current = window.location.pathname;
  if (PUBLIC_PAGE_PREFIXES.some((p) => current.startsWith(p))) return;
  if (redirecting) return;
  redirecting = true;
  const from = encodeURIComponent(current + window.location.search);
  window.location.assign(`/login?from=${from}`);
}

/* ------------------------------------------------------------------ */
/* Core request helper                                                 */
/* ------------------------------------------------------------------ */

type QueryValue = string | number | boolean | null | undefined;
export type QueryParams = Record<string, QueryValue>;

function buildUrl(path: string, query?: QueryParams): string {
  const url = `${BASE}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined || value === "") continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function parseError(response: Response): Promise<ApiError> {
  let code = "INTERNAL";
  let message = response.statusText || "Request failed";
  let fields: Record<string, string> | undefined;
  let requestId: string | undefined;
  try {
    const body: unknown = await response.json();
    if (body !== null && typeof body === "object" && "error" in body) {
      const err: unknown = body.error;
      if (err !== null && typeof err === "object") {
        if ("code" in err && typeof err.code === "string") code = err.code;
        if ("message" in err && typeof err.message === "string")
          message = err.message;
        if (
          "fields" in err &&
          err.fields !== null &&
          typeof err.fields === "object"
        ) {
          const raw = err.fields;
          const parsed: Record<string, string> = {};
          for (const [key, value] of Object.entries(raw)) {
            if (typeof value === "string") parsed[key] = value;
          }
          fields = parsed;
        }
        if ("requestId" in err && typeof err.requestId === "string")
          requestId = err.requestId;
      }
    }
  } catch {
    // Non-JSON error body — keep the status-based defaults.
  }
  return new ApiError({
    code,
    message,
    status: response.status,
    fields,
    requestId,
  });
}

interface RequestOptions {
  query?: QueryParams;
  signal?: AbortSignal;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  const isMutation = method !== "GET" && method !== "HEAD";
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (isMutation && csrfToken) headers["x-csrf-token"] = csrfToken;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      headers,
      credentials: "same-origin",
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: options.signal,
    });
  } catch (cause) {
    throw new ApiError({
      code: "NETWORK",
      message: "Network request failed",
      status: 0,
    });
  }

  if (response.status === 401) handleUnauthorized(path);
  if (response.status === 204) return undefined as T;

  if (!response.ok) throw await parseError(response);

  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/* ------------------------------------------------------------------ */
/* Typed helpers                                                       */
/* ------------------------------------------------------------------ */

export function apiGet<T>(path: string, options?: RequestOptions): Promise<T> {
  return request<T>("GET", path, undefined, options);
}

export function apiPost<T>(
  path: string,
  body?: unknown,
  options?: RequestOptions,
): Promise<T> {
  return request<T>("POST", path, body, options);
}

export function apiPatch<T>(
  path: string,
  body?: unknown,
  options?: RequestOptions,
): Promise<T> {
  return request<T>("PATCH", path, body, options);
}

export function apiPut<T>(
  path: string,
  body?: unknown,
  options?: RequestOptions,
): Promise<T> {
  return request<T>("PUT", path, body, options);
}

export function apiDelete<T>(
  path: string,
  options?: RequestOptions,
): Promise<T> {
  return request<T>("DELETE", path, undefined, options);
}

export function apiDeleteBody<T>(
  path: string,
  body: unknown,
  options?: RequestOptions,
): Promise<T> {
  return request<T>("DELETE", path, body, options);
}

export async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (csrfToken) headers["x-csrf-token"] = csrfToken;
  let response: Response;
  try {
    response = await fetch(buildUrl(path), {
      method: "POST",
      headers,
      credentials: "same-origin",
      body: form,
    });
  } catch {
    throw new ApiError({
      code: "NETWORK",
      message: "Network request failed",
      status: 0,
    });
  }
  if (response.status === 401) handleUnauthorized(path);
  if (!response.ok) throw await parseError(response);
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export async function apiPostBlob(path: string, body?: unknown): Promise<Blob> {
  const headers: Record<string, string> = { Accept: "application/pdf" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (csrfToken) headers["x-csrf-token"] = csrfToken;
  const response = await fetch(buildUrl(path), {
    method: "POST",
    headers,
    credentials: "same-origin",
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (response.status === 401) handleUnauthorized(path);
  if (!response.ok) throw await parseError(response);
  return response.blob();
}

/** Fetch a paginated list endpoint (matches shared `Page<T>`, spec §24.1). */
export function apiGetPage<T>(
  path: string,
  params: { page?: number; pageSize?: number } & QueryParams = {},
): Promise<Page<T>> {
  return apiGet<Page<T>>(path, { query: params });
}
